"use client";

import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { GradText, SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "./kit";
import { useEnterStagger } from "./kit/motion";
import { roadmapItem } from "@/lib/roadmap";
import { routePath } from "@/lib/routes";

const FAQS: { q: string; a: ReactNode }[] = [
  {
    q: "What makes rii.link different from a normal URL shortener?",
    a: "Most shorteners just redirect. On Premium, rii adds a visual routing layer — decide each visitor's destination by device, geo, language or time — and can return raw JSON, HTML or XML instead of a redirect, which makes a link usable by apps and AI agents, not just browsers.",
  },
  {
    q: "Can I use my own domain — and leave with it?",
    a: (
      <>
        Yes. Bring <span className="font-jetbrains text-brand-600">go.yourbrand.com</span>, point one record, and every
        link and QR carries your brand. The domain stays yours — re-point it any time. When you leave, export every link as JSON or CSV, or as
        nginx and Caddy rules to host the redirects yourself (rules those servers can&apos;t express, like geo
        or device splits, fall back to the link&apos;s default destination).
      </>
    ),
  },
  {
    q: "Is rii.link GDPR / DSGVO compliant?",
    a: "Yes. We are EU-first — visitor IPs anonymised before storage, and bots filtered out of your analytics by default. No selling of click data, ever.",
  },
  {
    q: "What's coming next?",
    // Dates derive from lib/roadmap.ts — the same source as every sash and
    // the changelog ledger, so this answer can't drift from them.
    a: (
      <>
        Email forwarding, the MCP server and workspace export all shipped — each on or ahead of its promised date; the{" "}
        <a href={routePath("changelog")} className="text-brand-600 hover:text-brand-700">
          changelog
        </a>{" "}
        keeps the receipts. Still coming: full nameserver delegation so you configure DNS once (
        {roadmapItem("nameservers").promised}) and outbound webhooks ({roadmapItem("webhooks").promised}). Sections
        marked &quot;In progress&quot; above show what&apos;s shipping.
      </>
    ),
  },
  {
    q: "Do you have a free plan?",
    a: "Yes — 100 active links, 2 custom domains, unmetered clicks, QR codes and 30-day analytics, no credit card required. Nothing resets monthly. Upgrade when you need smart routing, dynamic responses, more domains or seats.",
  },
];

export default function FaqSection() {
  const [open, setOpen] = useState<Set<number>>(new Set([0]));
  const listRef = useRef<HTMLDivElement>(null);

  // Only rendered on the homepage's grid-mode reel — no non-grid consumer, so
  // the plain pair (no null-ref gate) is enough. `data-snug`: the sheet's own
  // half-cell inset padding (md:px/py below) already provides the breathing
  // room a default block gets from the extra row, so no additional row is
  // needed on top of it.
  useWholeRowBlocks(listRef);
  useBandRowAlign(listRef);

  // Rows slide up in sequence as the list enters.
  useEnterStagger(listRef, "[data-faq-row]", { y: 18, duration: 0.6, stagger: 0.07 });

  const toggle = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <SectionShell divider>
      <SectionHead
        center
        eyebrow="FAQ"
        title={
          <>
            Questions, <GradText>answered.</GradText>
          </>
        }
      />

      {/* White "sheet" clearing — the list reads as a sheet of paper on the
          grid. Wider than the old 780px column (more grid columns) and padded
          by half a cell so the text never sits flush with the sheet's edge. */}
      <div
        ref={listRef}
        data-grid-block
        data-grid-fill="#ffffff"
        data-snug
        className="mx-auto max-w-[1008px] md:px-[calc(var(--grid-cell,72px)/2)] md:py-[calc(var(--grid-cell,72px)/2)]"
      >
          {FAQS.map((item, i) => {
            const isOpen = open.has(i);
            return (
              <div key={item.q} data-faq-row className="border-b border-line">
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 py-[22px] text-left text-[17px] font-semibold text-ink"
                >
                  {item.q}
                  <span
                    className={`grid h-[26px] w-[26px] shrink-0 place-items-center rounded-lg border transition duration-200 ${
                      isOpen
                        ? "rotate-180 border-transparent bg-brand-600 text-white"
                        : "border-line-2 text-muted"
                    }`}
                  >
                    ▾
                  </span>
                </button>
                <div
                  className={`grid overflow-hidden transition-all duration-300 ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="min-h-0">
                    <p className="mb-[22px] text-[15px] leading-[1.65] text-muted">{item.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </SectionShell>
  );
}
